import { SimpleGrid, Table } from "@mantine/core"
import { useEffect, useMemo, useState } from "react"
import { AuthenticatedAppShell } from "../../components/authenticated-app-shell"
import { Link } from "../../components/link"
import { AppCard, DataTableShell, EmptyState, MetricCard, SectionHeader } from "../../components/ui"
import {
  apiGet,
  type DashboardOverview,
  type SiteChannelRollupState,
  type SiteListSummary,
  type SiteRecord,
} from "../../lib/api"
import { formatDecimal, formatMicrosCurrency } from "../../lib/format"
import { goalLabel } from "../../lib/site-labels"

interface PortfolioTotals {
  spendMicros: number
  conversions: number
  clicks: number
  liveCampaigns: number
}

export function DashboardPage() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [sites, setSites] = useState<SiteRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void Promise.all([
      apiGet<DashboardOverview>("/api/dashboard"),
      apiGet<SiteRecord[]>("/api/sites"),
    ])
      .then(([overviewValue, sitesValue]) => {
        if (cancelled) return
        setOverview(overviewValue)
        setSites(sitesValue)
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : "Failed to load dashboard.")
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const totals = useMemo<PortfolioTotals>(() => aggregateTotals(sites), [sites])
  const topSites = useMemo(() => rankSitesBySpend(sites), [sites])
  const loading = sites === null
  const spendValue = loading ? "..." : formatMicrosCurrency(totals.spendMicros)

  return (
    <AuthenticatedAppShell eyebrow="Overview" title="Dashboard">
      {(session) => (
        <>
          {error ? (
            <EmptyState>
              <strong>Overview unavailable</strong>
              <br />
              {error}
            </EmptyState>
          ) : null}

          <SimpleGrid cols={{ base: 2, lg: 4 }}>
            <MetricCard label="Spend (30d)" value={spendValue} />
            <MetricCard
              label="Conversions (30d)"
              value={loading ? "..." : formatDecimal(totals.conversions)}
            />
            <MetricCard
              label="Clicks (30d)"
              value={loading ? "..." : formatDecimal(totals.clicks)}
            />
            <MetricCard
              label="Live campaigns"
              value={loading ? "..." : String(totals.liveCampaigns)}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 2, lg: 4 }}>
            <MetricCard label="Tracked sites" value={overview?.siteCount ?? "..."} />
            <MetricCard
              label="Connected channels"
              value={overview?.connectedChannelCount ?? "..."}
            />
            <MetricCard label="Active campaigns" value={overview?.activeCampaignCount ?? "..."} />
            <MetricCard label="Workspace" value={session.org?.name ?? "Adgiro"} />
          </SimpleGrid>

          <AppCard>
            <SectionHeader
              title="Top sites"
              description="Ranked by ad spend over the last 30 days."
              actions={
                <Link className="button button--ghost" href="/app/sites">
                  All sites
                </Link>
              }
            />
            {loading ? (
              <EmptyState>Loading sites…</EmptyState>
            ) : topSites.length === 0 ? (
              <EmptyState>Add a website to start tracking spend and performance.</EmptyState>
            ) : (
              <DataTableShell minWidth={720} mt="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Site</Table.Th>
                    <Table.Th>Spend 30d</Table.Th>
                    <Table.Th>Conv. 30d</Table.Th>
                    <Table.Th>CPA</Table.Th>
                    <Table.Th>Live</Table.Th>
                    <Table.Th>Channels</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {topSites.map((site) => (
                    <TopSiteRow key={site.siteId} site={site} />
                  ))}
                </Table.Tbody>
              </DataTableShell>
            )}
          </AppCard>

          <AppCard>
            <SectionHeader title="Latest optimization" />
            <p className="dashboard-optimization">
              {overview?.latestOptimizationSummary ?? "No optimization run recorded yet."}
            </p>
          </AppCard>
        </>
      )}
    </AuthenticatedAppShell>
  )
}

function TopSiteRow({ site }: { site: SiteRecord }) {
  const summary = site.summary
  const spendMicros = summary?.spendMicros30Days ?? 0
  const conversions = summary?.conversions30Days ?? 0
  const cpaMicros = conversions > 0 ? spendMicros / conversions : null
  const channels = summary?.channels

  return (
    <Table.Tr>
      <Table.Td>
        <Link className="table-name-link" href={`/app/sites/${site.siteId}`}>
          {site.name}
        </Link>
        <div className="dashboard-site-meta">
          <span>{site.primaryDomain ?? "Domain unset"}</span>
          <span>{goalLabel(site.primaryGoal)}</span>
        </div>
      </Table.Td>
      <Table.Td>{formatMicrosCurrency(spendMicros)}</Table.Td>
      <Table.Td>{formatDecimal(conversions)}</Table.Td>
      <Table.Td>{cpaMicros === null ? "-" : formatMicrosCurrency(cpaMicros)}</Table.Td>
      <Table.Td>{String(summary?.liveCampaignCount ?? 0)}</Table.Td>
      <Table.Td>
        <div className="site-channel-pills">
          <ChannelPill label="Search" state={channels?.googleSearch ?? "missing"} />
          <ChannelPill label="PMax" state={channels?.googlePmax ?? "missing"} />
          <ChannelPill label="Meta" state={channels?.metaAds ?? "missing"} />
        </div>
      </Table.Td>
    </Table.Tr>
  )
}

function ChannelPill({ label, state }: { label: string; state: SiteChannelRollupState }) {
  return (
    <span className={`site-channel-pill site-channel-pill--${state}`}>
      <strong>{label}</strong>
      <span>{channelStateLabel(state)}</span>
    </span>
  )
}

function channelStateLabel(state: SiteChannelRollupState): string {
  switch (state) {
    case "needs_sync":
      return "Needs sync"
    case "live":
      return "Live"
    case "paused":
      return "Paused"
    case "draft":
      return "Draft"
    case "failed":
      return "Failed"
    default:
      return "Missing"
  }
}

function aggregateTotals(sites: SiteRecord[] | null): PortfolioTotals {
  const totals: PortfolioTotals = { spendMicros: 0, conversions: 0, clicks: 0, liveCampaigns: 0 }
  if (!sites) return totals
  for (const site of sites) {
    const summary: SiteListSummary | undefined = site.summary
    if (!summary) continue
    totals.spendMicros += summary.spendMicros30Days
    totals.conversions += summary.conversions30Days
    totals.clicks += summary.clicks30Days
    totals.liveCampaigns += summary.liveCampaignCount
  }
  return totals
}

function rankSitesBySpend(sites: SiteRecord[] | null): SiteRecord[] {
  if (!sites) return []
  return [...sites]
    .sort((left, right) => {
      const leftSpend = left.summary?.spendMicros30Days ?? 0
      const rightSpend = right.summary?.spendMicros30Days ?? 0
      if (rightSpend !== leftSpend) return rightSpend - leftSpend
      const leftConv = left.summary?.conversions30Days ?? 0
      const rightConv = right.summary?.conversions30Days ?? 0
      return rightConv - leftConv
    })
    .slice(0, 6)
}
